import { ApolloError } from '@apollo/client';
import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { Tag01Icon } from '@/components/icons/Tag01Icon';
import { User01Icon } from '@/components/icons/User01Icon';
import { Users01Icon } from '@/components/icons/Users01Icon';
import { Dialog } from '@/components/modals/Dialog';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { PILL } from '@/styles/tokens/borderRadii';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';
import { OVERLAY_MD } from '@/styles/tokens/panelWidths';
import { zIndexes } from '@/styles/zIndexes';
import { FeatureFlagFlagStatus, FlagEnablementStatus } from '@/types/schema';
import { getEnvironment } from '@/utils/environmentUtils';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';
import { formatEnumCase } from '@/utils/formatting/strings';

import {
  FeatureFlagRow,
  formatEnablementStatus,
  formatTeamLabel,
} from './AdminFeatureFlagsPage.utils';
import {
  AdminFeatureFlagsPage_ReleaseGroupFragment,
  Operations,
  useFeatureFlagTenantEnablementsQuery,
  useSetFeatureFlagStatusMutation,
} from './graphql/AdminFeatureFlagsPage.generated';

interface FeatureFlagControlsDrawerProps {
  row: FeatureFlagRow;
  releaseGroups: AdminFeatureFlagsPage_ReleaseGroupFragment[];
  hasMoreReleaseGroups: boolean;
  releaseGroupTotalCount: number;
  isOpen: boolean;
  onClose: () => void;
}

type PendingAction =
  | { kind: 'global'; status: FlagEnablementStatus }
  | { kind: 'tenant'; status: FlagEnablementStatus }
  | {
      kind: 'releaseGroup';
      status: FlagEnablementStatus;
      releaseGroup: AdminFeatureFlagsPage_ReleaseGroupFragment;
    }
  | {
      kind: 'otherTenant';
      status: FlagEnablementStatus;
      tenant: { id: string; name: string };
    };

type ActionVerb = 'enable' | 'disable' | 'exclude' | 'clear';

interface TenantEnablementBadge {
  variant: BadgeVariants;
  label: string;
}

// Buckets the effective status into three visual categories so the per-tenant
// list distinguishes explicit overrides from environment defaults at a glance.
function describeTenantEnablement(
  enabled: boolean,
  status: FlagEnablementStatus
): TenantEnablementBadge {
  switch (status) {
    case FlagEnablementStatus.OnForTenant:
      return { variant: BadgeVariants.Success, label: 'On — tenant override' };
    case FlagEnablementStatus.OnForReleaseGroup:
      return { variant: BadgeVariants.Success, label: 'On — release group' };
    case FlagEnablementStatus.OnForEnv:
      return {
        variant: BadgeVariants.SecondaryOutline,
        label: 'On — env default',
      };
    case FlagEnablementStatus.Off:
      return {
        variant: BadgeVariants.SecondaryOutline,
        label: 'Off — env default',
      };
    case FlagEnablementStatus.OffForTenant:
      return {
        variant: BadgeVariants.Destructive,
        label: 'Excluded — tenant',
      };
    case FlagEnablementStatus.OffForReleaseGroup:
      return {
        variant: BadgeVariants.Destructive,
        label: 'Excluded — release group',
      };
    default:
      // CLEAR_* statuses aren't expected from an evaluation query; fall back
      // to the boolean signal so the badge remains meaningful.
      return enabled
        ? { variant: BadgeVariants.Success, label: 'On' }
        : { variant: BadgeVariants.SecondaryOutline, label: 'Off' };
  }
}

function actionVerbFor(status: FlagEnablementStatus): ActionVerb {
  switch (status) {
    case FlagEnablementStatus.OnForEnv:
    case FlagEnablementStatus.OnForTenant:
    case FlagEnablementStatus.OnForReleaseGroup:
      return 'enable';
    case FlagEnablementStatus.ClearTenant:
    case FlagEnablementStatus.ClearReleaseGroup:
      return 'clear';
    case FlagEnablementStatus.OffForTenant:
    case FlagEnablementStatus.OffForReleaseGroup:
      return 'exclude';
    default:
      return 'disable';
  }
}

function isEnabledStatus(status: FlagEnablementStatus): boolean {
  switch (status) {
    case FlagEnablementStatus.OnForEnv:
    case FlagEnablementStatus.OnForTenant:
    case FlagEnablementStatus.OnForReleaseGroup:
      return true;
    default:
      return false;
  }
}

export function FeatureFlagControlsDrawer({
  row,
  releaseGroups,
  hasMoreReleaseGroups,
  releaseGroupTotalCount,
  isOpen,
  onClose,
}: FeatureFlagControlsDrawerProps) {
  const { showFeedback, createErrorFeedback } = useFeedback();
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    () => new Set()
  );

  const toggleExpandedGroup = (id: string) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const [setFlagStatus, { loading, error }] = useSetFeatureFlagStatusMutation({
    onError: createErrorFeedback(
      'Failed to update the flag status. Please refresh and try again.'
    ),
    refetchQueries: [
      Operations.Query.AdminFeatureFlagsPage,
      Operations.Query.FeatureFlagTenantEnablements,
    ],
    awaitRefetchQueries: true,
  });

  useReportError('error updating feature flag status (v2)', error);

  const { id: currentTenantId } = useTenantDetailsContext();
  const {
    data: tenantEnablementsData,
    loading: tenantEnablementsLoading,
    error: tenantEnablementsError,
  } = useFeatureFlagTenantEnablementsQuery({
    variables: { name: row.name },
    fetchPolicy: 'cache-and-network',
  });

  useReportError(
    'error loading per-tenant feature flag enablements',
    tenantEnablementsError
  );

  const tenantEnablements = useMemo(
    () => tenantEnablementsData?.featureFlagTenantEnablements ?? [],
    [tenantEnablementsData?.featureFlagTenantEnablements]
  );

  const currentTenantEnablement = useMemo(
    () => tenantEnablements.find((e) => e.tenant.id === currentTenantId),
    [tenantEnablements, currentTenantId]
  );
  const currentTenantEnablementStatus =
    currentTenantEnablement?.enablementStatus ?? row.enablementStatus;
  const tenantOverride: 'on' | 'off' | null = (() => {
    const s = currentTenantEnablementStatus;
    if (s === FlagEnablementStatus.OnForTenant) return 'on';
    if (s === FlagEnablementStatus.OffForTenant) return 'off';
    return null;
  })();

  // Global env-level state, independent of per-tenant overrides. Drives which
  // RG affordance (Include vs Exclude) is offered and which tenant affordance
  // is offered when there's no override yet.
  const isGlobalOn = row.flag.flagStatus === FeatureFlagFlagStatus.On;

  const onReleaseGroupIds = useMemo(
    () => new Set(row.onReleaseGroups.map((rg) => rg.id)),
    [row.onReleaseGroups]
  );
  const offReleaseGroupIds = useMemo(
    () => new Set(row.offReleaseGroups.map((rg) => rg.id)),
    [row.offReleaseGroups]
  );

  const effectiveOnForTenant =
    currentTenantEnablement?.enabled ??
    isEnabledStatus(currentTenantEnablementStatus);

  const globalOptions = useMemo(
    () => [
      { display: 'Off for all', value: FlagEnablementStatus.Off },
      { display: 'On for all', value: FlagEnablementStatus.OnForEnv },
    ],
    []
  );

  const executeStatusChange = (
    status: FlagEnablementStatus,
    options: { releaseGroupId?: string; tenantId?: string } = {}
  ) => {
    void setFlagStatus({
      variables: {
        name: row.name,
        status,
        releaseGroupId: options.releaseGroupId,
        tenantId: options.tenantId,
      },
      onCompleted: () => {
        showFeedback(
          `Updated ${row.name} to "${formatEnablementStatus(
            status
          )}" for ${getEnvironment()}. Changes take up to 30 seconds to propagate.`,
          { variant: 'success' }
        );
      },
    });
  };

  const runMutation = (action: PendingAction) => {
    if (action.kind === 'releaseGroup') {
      executeStatusChange(action.status, {
        releaseGroupId: action.releaseGroup.id,
      });
      return;
    }
    if (action.kind === 'otherTenant') {
      executeStatusChange(action.status, { tenantId: action.tenant.id });
      return;
    }
    if (action.kind === 'tenant') {
      // Pass tenantId explicitly so tenant-scoped statuses (OnForTenant,
      // OffForTenant, ClearTenant) target the admin's current tenant. The V1
      // page relied on the backend inferring the tenant when tenantId is
      // omitted; passing it here keeps the mutation unambiguous.
      executeStatusChange(action.status, { tenantId: currentTenantId });
      return;
    }
    executeStatusChange(action.status);
  };

  const handleGlobalChange = (next: FlagEnablementStatus) => {
    const current = isGlobalOn
      ? FlagEnablementStatus.OnForEnv
      : FlagEnablementStatus.Off;
    if (next === current) return;
    setPendingAction({ kind: 'global', status: next });
  };

  const handleTenantAction = (next: FlagEnablementStatus) => {
    setPendingAction({ kind: 'tenant', status: next });
  };

  const handleReleaseGroupAction = (
    releaseGroup: AdminFeatureFlagsPage_ReleaseGroupFragment,
    status: FlagEnablementStatus
  ) => {
    setPendingAction({ kind: 'releaseGroup', status, releaseGroup });
  };

  const handleOtherTenantAction = (
    tenant: { id: string; name: string },
    status: FlagEnablementStatus
  ) => {
    setPendingAction({ kind: 'otherTenant', status, tenant });
  };

  const handleConfirm = (reason: 'confirm' | 'cancel') => {
    const action = pendingAction;
    setPendingAction(null);
    if (reason === 'confirm' && action) {
      runMutation(action);
    }
  };

  const body = (
    <Stack spacing={3} width="100%" pt={2}>
      <Box
        sx={{
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${COLORS.GRAY[200]}`,
          bgcolor: COLORS.GRAY[50],
        }}
      >
        <Stack spacing={2}>
          {row.description && (
            <Typography variant="body-xs">{row.description}</Typography>
          )}
          <Stack direction="row" flexWrap="wrap" rowGap={2} columnGap={4}>
            <MetaItem
              label="Created"
              value={formatDateToMonDDYYYY(row.createdAt)}
            />
            <MetaItem label="Owner" value={row.owner || '—'} />
            {row.team && (
              <MetaItem
                label="Team"
                value={
                  <Badge
                    variant={BadgeVariants.Accent}
                    display={formatTeamLabel(row.team)}
                  />
                }
              />
            )}
            {row.group && (
              <MetaItem label="Group" value={formatEnumCase(row.group)} />
            )}
          </Stack>
        </Stack>
      </Box>

      <Divider />

      <Stack spacing={1.5}>
        <SectionLabel>Global status</SectionLabel>
        <ButtonGroup
          label=""
          disabled={loading}
          options={globalOptions}
          value={
            isGlobalOn
              ? FlagEnablementStatus.OnForEnv
              : FlagEnablementStatus.Off
          }
          onChange={(_, value) => handleGlobalChange(value)}
        />
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <SectionLabel icon={<User01Icon size={16} />}>
          Current tenant override
        </SectionLabel>
        <TenantOverrideControls
          tenantOverride={tenantOverride}
          effectiveOnForTenant={effectiveOnForTenant}
          loading={loading}
          onSetOn={() => handleTenantAction(FlagEnablementStatus.OnForTenant)}
          onSetOff={() => handleTenantAction(FlagEnablementStatus.OffForTenant)}
          onClear={() => handleTenantAction(FlagEnablementStatus.ClearTenant)}
        />
        <Typography variant="body-xs">
          {tenantOverride
            ? 'Clear removes the tenant override only — release-group rules are left in place.'
            : effectiveOnForTenant
              ? 'This tenant is currently on. Add a tenant override to exclude it.'
              : 'This tenant is currently off. Add a tenant override to enable it.'}
        </Typography>
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <SectionLabel icon={<Tag01Icon size={16} />}>
          Release groups
        </SectionLabel>
        <Typography variant="body-xs">
          {isGlobalOn
            ? '"On for all" is set — exclude a release group to force it off.'
            : 'Include a release group to turn the flag on for its tenants.'}
        </Typography>
        {hasMoreReleaseGroups && (
          <Callout severity="warning">
            Showing {releaseGroups.length} of {releaseGroupTotalCount} release
            groups. Some groups are not listed here — contact engineering to
            manage flags for missing groups.
          </Callout>
        )}
        {releaseGroups.length === 0 ? (
          <Typography variant="body-xs">
            No release groups available.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {releaseGroups.map((releaseGroup) => (
              <ReleaseGroupRow
                key={releaseGroup.id}
                releaseGroup={releaseGroup}
                isOn={onReleaseGroupIds.has(releaseGroup.id)}
                isOff={offReleaseGroupIds.has(releaseGroup.id)}
                isGlobalOn={isGlobalOn}
                disabled={loading}
                isExpanded={expandedGroupIds.has(releaseGroup.id)}
                onToggleExpanded={() => toggleExpandedGroup(releaseGroup.id)}
                onClickInclude={() =>
                  handleReleaseGroupAction(
                    releaseGroup,
                    FlagEnablementStatus.OnForReleaseGroup
                  )
                }
                onClickExclude={() =>
                  handleReleaseGroupAction(
                    releaseGroup,
                    FlagEnablementStatus.OffForReleaseGroup
                  )
                }
                onClickClear={() =>
                  handleReleaseGroupAction(
                    releaseGroup,
                    FlagEnablementStatus.ClearReleaseGroup
                  )
                }
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Divider />

      <PerTenantSection
        enablements={tenantEnablements}
        currentTenantId={currentTenantId}
        loading={tenantEnablementsLoading}
        mutationLoading={loading}
        error={tenantEnablementsError}
        onIncludeTenant={(tenant) =>
          handleOtherTenantAction(tenant, FlagEnablementStatus.OnForTenant)
        }
        onExcludeTenant={(tenant) =>
          handleOtherTenantAction(tenant, FlagEnablementStatus.OffForTenant)
        }
        onClearTenant={(tenant) =>
          handleOtherTenantAction(tenant, FlagEnablementStatus.ClearTenant)
        }
      />
    </Stack>
  );

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        heading={row.name}
        width={OVERLAY_MD}
        body={body}
      />
      {pendingAction && (
        <FeatureFlagConfirmationModal
          action={pendingAction}
          flagName={row.name}
          loading={loading}
          onClose={handleConfirm}
        />
      )}
    </>
  );
}

interface MetaItemProps {
  label: string;
  value: ReactNode;
}

function MetaItem({ label, value }: MetaItemProps) {
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Typography variant="eyebrow-xs" sx={{ color: COLORS.TEXT.SECONDARY }}>
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant="body-xs">{value}</Typography>
      ) : (
        value
      )}
    </Stack>
  );
}

interface SectionLabelProps {
  icon?: ReactNode;
  children: ReactNode;
}

function SectionLabel({ icon, children }: SectionLabelProps) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{ color: COLORS.TEXT.SECONDARY }}
    >
      {icon}
      <Typography variant="eyebrow-xs">{children}</Typography>
    </Stack>
  );
}

interface TenantOverrideControlsProps {
  tenantOverride: 'on' | 'off' | null;
  /**
   * Effective on/off for the tenant in the absence of a tenant override.
   * Drives which fresh affordance is offered: if the tenant would be on
   * anyway, only "Off for tenant" is meaningful, and vice versa.
   */
  effectiveOnForTenant: boolean;
  loading: boolean;
  onSetOn: () => void;
  onSetOff: () => void;
  onClear: () => void;
}

function TenantOverrideControls({
  tenantOverride,
  effectiveOnForTenant,
  loading,
  onSetOn,
  onSetOff,
  onClear,
}: TenantOverrideControlsProps) {
  const hasOverride = tenantOverride !== null;
  const pillKind: 'include' | 'exclude' = (() => {
    if (tenantOverride === 'on') return 'include';
    if (tenantOverride === 'off') return 'exclude';
    return effectiveOnForTenant ? 'exclude' : 'include';
  })();

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {hasOverride && (
        <ButtonBase
          onClick={onClear}
          disabled={loading}
          sx={{
            color: COLORS.TEXT.SECONDARY,
            fontSize: '0.75rem',
            fontWeight: 500,
            textDecoration: 'underline',
            '&:hover': { color: COLORS.GRAY[700] },
            '&.Mui-disabled': { opacity: 0.5 },
          }}
        >
          Clear
        </ButtonBase>
      )}
      <ReleaseGroupPill
        label={pillKind === 'include' ? 'Include' : 'Exclude'}
        active={hasOverride}
        activeVariant={pillKind === 'include' ? 'success' : 'danger'}
        disabled={loading || hasOverride}
        onClick={pillKind === 'include' ? onSetOn : onSetOff}
      />
    </Stack>
  );
}

interface ReleaseGroupRowProps {
  releaseGroup: AdminFeatureFlagsPage_ReleaseGroupFragment;
  isOn: boolean;
  isOff: boolean;
  /** Global env-level on/off — decides which fresh affordance is offered. */
  isGlobalOn: boolean;
  disabled: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onClickInclude: () => void;
  onClickExclude: () => void;
  onClickClear: () => void;
}

function ReleaseGroupRow({
  releaseGroup,
  isOn,
  isOff,
  isGlobalOn,
  disabled,
  isExpanded,
  onToggleExpanded,
  onClickInclude,
  onClickExclude,
  onClickClear,
}: ReleaseGroupRowProps) {
  const sortedTenants = useMemo(
    () =>
      [...(releaseGroup.tenants ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [releaseGroup.tenants]
  );
  const tenantCount = sortedTenants.length;
  const borderColor = (() => {
    if (isOn) return COLORS.FUNCTIONAL.SUCCESS[400];
    if (isOff) return COLORS.FUNCTIONAL.ERROR[400];
    return COLORS.GRAY[200];
  })();
  const hasOverride = isOn || isOff;
  const canExpand = tenantCount > 0;

  // Single pill per row. If the RG already has an override, the pill reflects
  // that override (depressed) and Clear is offered. If not, the pill offers
  // the action that's meaningful given the global state — Include when off
  // globally, Exclude when on globally.
  const pillKind: 'include' | 'exclude' = (() => {
    if (isOn) return 'include';
    if (isOff) return 'exclude';
    return isGlobalOn ? 'exclude' : 'include';
  })();

  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: `1px solid ${borderColor}`,
        bgcolor: COLORS.PRIMITIVES.WHITE,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          width: '100%',
          px: 1.5,
          py: 1.25,
        }}
      >
        <ButtonBase
          onClick={onToggleExpanded}
          disabled={!canExpand}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? 'Collapse tenant list' : 'Expand tenant list'
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            borderRadius: '4px',
            py: 0.25,
            '&.Mui-disabled': { opacity: 1, cursor: 'default' },
          }}
        >
          <ChevronRightIcon
            size={16}
            sx={{
              color: canExpand ? COLORS.TEXT.SECONDARY : COLORS.GRAY[300],
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
              flexShrink: 0,
            }}
          />
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="title-xs">
              {formatEnumCase(releaseGroup.name)}
            </Typography>
            <Typography variant="body-xs">
              {tenantCount} {tenantCount === 1 ? 'tenant' : 'tenants'}
            </Typography>
          </Stack>
        </ButtonBase>
        <Stack direction="row" spacing={1} alignItems="center">
          {hasOverride && (
            <ButtonBase
              onClick={onClickClear}
              disabled={disabled}
              sx={{
                color: COLORS.TEXT.SECONDARY,
                fontSize: '0.75rem',
                fontWeight: 500,
                textDecoration: 'underline',
                '&:hover': { color: COLORS.GRAY[700] },
                '&.Mui-disabled': { opacity: 0.5 },
              }}
            >
              Clear
            </ButtonBase>
          )}
          <ReleaseGroupPill
            label={pillKind === 'include' ? 'Include' : 'Exclude'}
            active={hasOverride}
            activeVariant={pillKind === 'include' ? 'success' : 'danger'}
            disabled={disabled || hasOverride}
            onClick={pillKind === 'include' ? onClickInclude : onClickExclude}
          />
        </Stack>
      </Box>
      {isExpanded && canExpand && (
        <Box
          sx={{
            borderTop: `1px solid ${COLORS.GRAY[200]}`,
            bgcolor: COLORS.GRAY[50],
            px: 1.5,
            py: 1,
            pl: 4.25,
          }}
        >
          <Stack spacing={0.25}>
            {sortedTenants.map((tenant) => (
              <Typography
                key={tenant.id}
                variant="body-xs"
                sx={{ color: COLORS.GRAY[700] }}
              >
                {tenant.name}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

interface ReleaseGroupPillProps {
  label: string;
  active: boolean;
  activeVariant: 'success' | 'danger';
  disabled: boolean;
  onClick: () => void;
}

function ReleaseGroupPill({
  label,
  active,
  activeVariant,
  disabled,
  onClick,
}: ReleaseGroupPillProps) {
  const palette =
    activeVariant === 'success'
      ? COLORS.FUNCTIONAL.SUCCESS
      : COLORS.FUNCTIONAL.ERROR;

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        borderRadius: PILL,
        px: 1.5,
        py: 0.5,
        border: `1px solid ${active ? palette[400] : COLORS.GRAY[200]}`,
        bgcolor: active ? palette[100] : COLORS.PRIMITIVES.WHITE,
        color: active ? palette[700] : COLORS.GRAY[700],
        fontSize: '0.75rem',
        fontWeight: 600,
        boxShadow: active ? `inset 0 1px 2px ${palette[200]}` : 'none',
        '&:hover': {
          bgcolor: active ? palette[100] : COLORS.GRAY[100],
        },
        // When a pill is active, the disabled state is intentional (depressed
        // toggle) — keep full opacity so it reads as "on" rather than greyed
        // out. Inactive disabled pills still fade.
        '&.Mui-disabled': { opacity: active ? 1 : 0.5 },
      }}
    >
      {active ? `✓ ${label}` : label}
    </ButtonBase>
  );
}

interface PerTenantSectionProps {
  enablements: Array<{
    enabled: boolean;
    enablementStatus: FlagEnablementStatus;
    tenant: { id: string; name: string };
  }>;
  currentTenantId: string;
  loading: boolean;
  mutationLoading: boolean;
  error: ApolloError | undefined;
  onIncludeTenant: (tenant: { id: string; name: string }) => void;
  onExcludeTenant: (tenant: { id: string; name: string }) => void;
  onClearTenant: (tenant: { id: string; name: string }) => void;
}

const PER_TENANT_PAGE_SIZE = 10;

function PerTenantSection({
  enablements,
  currentTenantId,
  loading,
  mutationLoading,
  error,
  onIncludeTenant,
  onExcludeTenant,
  onClearTenant,
}: PerTenantSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const { onCount, offCount } = useMemo(() => {
    let on = 0;
    let off = 0;
    for (const e of enablements) {
      if (e.enabled) {
        on++;
      } else {
        off++;
      }
    }
    return { onCount: on, offCount: off };
  }, [enablements]);

  // Float tenants with an explicit override (tenant-level or release-group)
  // above those inheriting the env default, then sort alphabetically within
  // each group.
  const sortedRows = useMemo(
    () =>
      [...enablements].sort((a, b) => {
        const aIsDefault =
          a.enablementStatus === FlagEnablementStatus.OnForEnv ||
          a.enablementStatus === FlagEnablementStatus.Off;
        const bIsDefault =
          b.enablementStatus === FlagEnablementStatus.OnForEnv ||
          b.enablementStatus === FlagEnablementStatus.Off;
        if (aIsDefault !== bIsDefault) return aIsDefault ? 1 : -1;
        return a.tenant.name.localeCompare(b.tenant.name);
      }),
    [enablements]
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!normalizedQuery) return sortedRows;
    return sortedRows.filter((row) =>
      row.tenant.name.toLowerCase().includes(normalizedQuery)
    );
  }, [sortedRows, normalizedQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / PER_TENANT_PAGE_SIZE)
  );
  // Reset to page 1 whenever the filter changes the row set out from under us,
  // and clamp if the result count shrinks below the current page.
  useEffect(() => {
    setPage(1);
  }, [normalizedQuery]);
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const startIndex = (page - 1) * PER_TENANT_PAGE_SIZE;
  const visibleRows = filteredRows.slice(
    startIndex,
    startIndex + PER_TENANT_PAGE_SIZE
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const showSearch = sortedRows.length > 0 || normalizedQuery.length > 0;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <SectionLabel icon={<Users01Icon size={16} />}>
          Per-tenant status
        </SectionLabel>
        {enablements.length > 0 && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Badge variant={BadgeVariants.Success} display={`${onCount} On`} />
            <Badge
              variant={BadgeVariants.SecondaryOutline}
              display={`${offCount} Off`}
            />
          </Stack>
        )}
      </Stack>
      {error && (
        <Callout severity="error">
          Couldn&apos;t load per-tenant enablement. Refresh to retry.
        </Callout>
      )}
      {loading && sortedRows.length === 0 && (
        <Typography variant="body-xs">Loading per-tenant status…</Typography>
      )}
      {!loading && !error && sortedRows.length === 0 && (
        <Typography variant="body-xs">No tenants found.</Typography>
      )}
      {showSearch && (
        <TextInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search tenants…"
          label="Search tenants"
          hideLabel
          fullWidth
          startAdornment={<SearchSmIcon size={16} />}
        />
      )}
      {sortedRows.length > 0 && filteredRows.length === 0 && (
        <Typography variant="body-xs">
          No tenants match “{searchQuery.trim()}”.
        </Typography>
      )}
      {filteredRows.length > 0 && (
        <Box
          sx={{
            borderRadius: '8px',
            border: `1px solid ${COLORS.GRAY[200]}`,
            overflow: 'hidden',
          }}
        >
          <Stack divider={<Divider />}>
            {visibleRows.map((entry) => {
              const badge = describeTenantEnablement(
                entry.enabled,
                entry.enablementStatus
              );
              const isCurrentTenant = entry.tenant.id === currentTenantId;
              const tenantOverride: 'on' | 'off' | null = (() => {
                if (entry.enablementStatus === FlagEnablementStatus.OnForTenant)
                  return 'on';
                if (
                  entry.enablementStatus === FlagEnablementStatus.OffForTenant
                )
                  return 'off';
                return null;
              })();
              return (
                <Stack
                  key={entry.tenant.id}
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 1.5, py: 1 }}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography variant="title-xs">
                      {entry.tenant.name}
                      {isCurrentTenant && (
                        <Typography
                          component="span"
                          variant="body-xs"
                          sx={{ ml: 0.75 }}
                        >
                          (current)
                        </Typography>
                      )}
                    </Typography>
                    <Badge variant={badge.variant} display={badge.label} />
                  </Stack>
                  <TenantOverridePill
                    tenantOverride={tenantOverride}
                    effectiveEnabled={entry.enabled}
                    disabled={mutationLoading}
                    onInclude={() => onIncludeTenant(entry.tenant)}
                    onExclude={() => onExcludeTenant(entry.tenant)}
                    onClear={() => onClearTenant(entry.tenant)}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}
      {filteredRows.length > PER_TENANT_PAGE_SIZE && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          startIndex={startIndex}
          visibleCount={visibleRows.length}
          totalCount={filteredRows.length}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </Stack>
  );
}

interface TenantOverridePillProps {
  tenantOverride: 'on' | 'off' | null;
  /** Effective enabled value for the tenant — drives the offered pill when
   *  there is no override yet. */
  effectiveEnabled: boolean;
  disabled: boolean;
  onInclude: () => void;
  onExclude: () => void;
  onClear: () => void;
}

function TenantOverridePill({
  tenantOverride,
  effectiveEnabled,
  disabled,
  onInclude,
  onExclude,
  onClear,
}: TenantOverridePillProps) {
  const hasOverride = tenantOverride !== null;
  const pillKind: 'include' | 'exclude' = (() => {
    if (tenantOverride === 'on') return 'include';
    if (tenantOverride === 'off') return 'exclude';
    return effectiveEnabled ? 'exclude' : 'include';
  })();

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {hasOverride && (
        <ButtonBase
          onClick={onClear}
          disabled={disabled}
          sx={{
            color: COLORS.TEXT.SECONDARY,
            fontSize: '0.75rem',
            fontWeight: 500,
            textDecoration: 'underline',
            '&:hover': { color: COLORS.GRAY[700] },
            '&.Mui-disabled': { opacity: 0.5 },
          }}
        >
          Clear
        </ButtonBase>
      )}
      <ReleaseGroupPill
        label={pillKind === 'include' ? 'Include' : 'Exclude'}
        active={hasOverride}
        activeVariant={pillKind === 'include' ? 'success' : 'danger'}
        disabled={disabled || hasOverride}
        onClick={pillKind === 'include' ? onInclude : onExclude}
      />
    </Stack>
  );
}

interface PaginationControlsProps {
  page: number;
  totalPages: number;
  startIndex: number;
  visibleCount: number;
  totalCount: number;
  onPrev: () => void;
  onNext: () => void;
}

function PaginationControls({
  page,
  totalPages,
  startIndex,
  visibleCount,
  totalCount,
  onPrev,
  onNext,
}: PaginationControlsProps) {
  const rangeStart = totalCount === 0 ? 0 : startIndex + 1;
  const rangeEnd = startIndex + visibleCount;
  const atFirst = page <= 1;
  const atLast = page >= totalPages;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ pt: 0.5 }}
    >
      <Typography variant="body-xs">
        Showing {rangeStart}–{rangeEnd} of {totalCount}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <PaginationIconButton
          ariaLabel="Previous page"
          disabled={atFirst}
          onClick={onPrev}
        >
          <ChevronLeftIcon size={16} />
        </PaginationIconButton>
        <Typography variant="body-xs" sx={{ color: COLORS.GRAY[700] }}>
          {page} / {totalPages}
        </Typography>
        <PaginationIconButton
          ariaLabel="Next page"
          disabled={atLast}
          onClick={onNext}
        >
          <ChevronRightIcon size={16} />
        </PaginationIconButton>
      </Stack>
    </Stack>
  );
}

interface PaginationIconButtonProps {
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}

function PaginationIconButton({
  ariaLabel,
  disabled,
  onClick,
  children,
}: PaginationIconButtonProps) {
  return (
    <ButtonBase
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        borderRadius: '6px',
        color: COLORS.GRAY[700],
        border: `1px solid ${COLORS.GRAY[200]}`,
        bgcolor: COLORS.PRIMITIVES.WHITE,
        '&:hover': { bgcolor: COLORS.GRAY[100] },
        '&.Mui-disabled': { opacity: 0.4 },
      }}
    >
      {children}
    </ButtonBase>
  );
}

interface FeatureFlagConfirmationModalProps {
  action: PendingAction;
  flagName: string;
  loading: boolean;
  onClose: (reason: 'confirm' | 'cancel') => void;
}

function FeatureFlagConfirmationModal({
  action,
  flagName,
  loading,
  onClose,
}: FeatureFlagConfirmationModalProps) {
  const verb = actionVerbFor(action.status);

  const tenantsValue = (() => {
    if (action.kind === 'releaseGroup') {
      const names = (action.releaseGroup.tenants ?? []).map((t) => t.name);
      return names.length > 0 ? names.join(', ') : 'No tenants in this group';
    }
    if (action.kind === 'global') return 'All tenants';
    if (action.kind === 'otherTenant') return action.tenant.name;
    return 'Current tenant';
  })();

  const scopeLabel = (() => {
    if (action.kind === 'global') return 'all tenants';
    if (action.kind === 'tenant') return 'the current tenant';
    if (action.kind === 'otherTenant') return action.tenant.name;
    return `the "${formatEnumCase(action.releaseGroup.name)}" release group`;
  })();

  const verbDescription = (() => {
    if (verb === 'clear') return 'clear the override on';
    if (verb === 'exclude') return 'explicitly exclude';
    return verb;
  })();
  const confirmVariant = verb === 'enable' ? 'primary' : 'destructive-primary';
  const confirmLabel = (() => {
    if (verb === 'enable') return 'Enable';
    if (verb === 'clear') return 'Clear';
    if (verb === 'exclude') return 'Exclude';
    return 'Disable';
  })();
  const exclusionHint =
    verb === 'exclude'
      ? `${flagName} will be forced off for ${scopeLabel}, even if "On for all" is set.`
      : null;

  return (
    <Dialog
      open
      onClose={() => onClose('cancel')}
      PaperProps={{ sx: { width: 400, borderRadius: '12px' } }}
      // This app's theme sets zIndex.drawer above zIndex.modal, so the modal
      // would otherwise render behind the SideDrawer it's launched from.
      sx={{ zIndex: zIndexes.POPOVER }}
    >
      <Stack spacing={2.5} sx={{ p: 3 }}>
        <Stack spacing={1}>
          <Typography variant="title-s" fontWeight={FONT_WEIGHTS.bold}>
            Confirm feature flag update
          </Typography>
          <Typography variant="body-xs">
            You are about to {verbDescription} <strong>{flagName}</strong> for{' '}
            {scopeLabel}.
          </Typography>
          {exclusionHint && (
            <Typography variant="body-xs">{exclusionHint}</Typography>
          )}
        </Stack>

        <Stack
          spacing={1}
          sx={{
            p: 1.5,
            borderRadius: '8px',
            border: `1px solid ${COLORS.GRAY[200]}`,
            bgcolor: COLORS.GRAY[50],
          }}
        >
          <SummaryRow
            label="Status"
            value={formatEnablementStatus(action.status)}
          />
          <SummaryRow label="Environment" value={getEnvironment()} />
          <SummaryRow label="Tenants" value={tenantsValue} />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            disabled={loading}
            onClick={() => onClose('cancel')}
          >
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            size="sm"
            fullWidth
            loading={loading}
            onClick={() => onClose('confirm')}
          >
            {confirmLabel}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
}

function SummaryRow({ label, value }: SummaryRowProps) {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between">
      <Typography variant="body-xs">{label}</Typography>
      <Typography variant="body-xs" sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}
